import { Skeleton } from '@/components/ui/skeleton';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { Send } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { type FlightsSourceFilter, useFilteredFlights, useFlightsStore } from './FlightsStore';
import { FLIGHT_COLUMNS, STATUS_TONE } from './columns';

export function FlightsTable() {
  const { data, isPending, isError, refetch } = useFilteredFlights();
  const serial = useFlightsStore((s) => s.selectedSerial);
  const sourceFilter = useFlightsStore((s) => s.sourceFilter);
  const openFlight = useFlightsStore((s) => s.openFlight);

  const forAircraft = useMemo(
    () => (data ?? []).filter((f) => f.aircraft_serial === serial),
    [data, serial],
  );
  const rows = useMemo(
    () => forAircraft.filter((f) => sourceFilter === 'all' || f.source === sourceFilter),
    [forAircraft, sourceFilter],
  );

  if (!serial) return <Placeholder>Select an aircraft to view its flights</Placeholder>;

  if (isPending) {
    return (
      <div className="p-3 space-y-2">
        {['r0', 'r1', 'r2', 'r3', 'r4'].map((k) => (
          <Skeleton key={k} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-[0.846em]">
        <span className="text-(--color-red)">Failed to load flights.</span>
        <button type="button" onClick={() => refetch()} className="text-(--color-green) underline">
          Retry
        </button>
      </div>
    );
  }

  // These are two DIFFERENT situations and get different copy — the mockup
  // conflated them into one message.
  if (forAircraft.length === 0) {
    return <Placeholder>No flights recorded for this aircraft.</Placeholder>;
  }
  if (rows.length === 0) {
    return <Placeholder>No flights match the current filter.</Placeholder>;
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-[0.769em] border-collapse">
        <thead className="sticky top-0 bg-(--color-surface)">
          <tr className="border-b border-(--color-border)">
            {FLIGHT_COLUMNS.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-2.5 py-2 font-bold text-(--color-text3) whitespace-nowrap',
                  c.align === 'right' ? 'text-right' : 'text-left',
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.flight_id}
              tabIndex={0}
              onClick={() => openFlight(f.flight_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openFlight(f.flight_id);
                }
              }}
              className="border-b border-(--color-border) cursor-pointer hover:bg-(--color-bg)"
            >
              {FLIGHT_COLUMNS.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-2.5 py-2 whitespace-nowrap',
                    c.align === 'right' ? 'text-right' : 'text-left',
                    c.key === 'status' && `font-bold ${STATUS_TONE[f.flight_status]}`,
                  )}
                >
                  {c.render(f)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 text-(--color-text3)">
      <Send size={26} strokeWidth={1.3} />
      <span className="text-[0.846em]">{children}</span>
    </div>
  );
}

/** The panel header's All / Historical / Planned chips.
 *
 *  Named `SourceFilterChips`, NOT `FlightsSourceFilter`: that name is already
 *  the store's exported filter TYPE, and two same-named exports across
 *  sibling modules is a trap for whoever imports both. */
export function SourceFilterChips() {
  const sourceFilter = useFlightsStore((s) => s.sourceFilter);
  const setSourceFilter = useFlightsStore((s) => s.setSourceFilter);
  const options: Array<[FlightsSourceFilter, string]> = [
    ['all', 'All'],
    ['historical', 'Historical'],
    ['planned', 'Planned'],
  ];
  return (
    <div className="flex gap-1">
      {options.map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => setSourceFilter(value)}
          className={cn(
            'text-[0.692em] px-2 py-0.5 rounded-full border transition-colors',
            sourceFilter === value
              ? 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)'
              : 'border-(--color-border) text-(--color-text3)',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Server-side filter controls (spec §2.1, added 2026-08-27).
 *
 * Kept visually distinct from `SourceFilterChips` on purpose: those narrow a
 * list already in memory, these change what the API is asked for and trigger a
 * refetch. All three default to unset, so the context loads exactly as it did
 * before until the user picks one.
 *
 * Live-mode only. In demo, MSW serves the fixture list whole and ignores query
 * params, so rendering controls that visibly did nothing would be a lie.
 */
export function ServerFilterControls() {
  const windowDays = useFlightsStore((s) => s.windowDays);
  const setWindowDays = useFlightsStore((s) => s.setWindowDays);
  const utmState = useFlightsStore((s) => s.utmState);
  const setUtmState = useFlightsStore((s) => s.setUtmState);
  const connector = useFlightsStore((s) => s.connector);
  const setConnector = useFlightsStore((s) => s.setConnector);

  if (config.isMock) return null;

  return (
    <div className="flex items-center gap-1.5">
      <FilterSelect
        label="Window"
        value={windowDays == null ? '' : String(windowDays)}
        onChange={(v) => setWindowDays(v === '' ? null : (Number(v) as 7 | 30 | 90))}
        options={[
          ['', 'All time'],
          ['7', 'Last 7 days'],
          ['30', 'Last 30 days'],
          ['90', 'Last 90 days'],
        ]}
      />
      <FilterSelect
        label="UTM state"
        value={utmState ?? ''}
        onChange={(v) => setUtmState(v === '' ? null : v)}
        options={[
          ['', 'Any state'],
          ['active', 'Active'],
          ['ended', 'Ended'],
          ['cancelled', 'Cancelled'],
          ['rejected', 'Rejected'],
          ['held', 'Held (RTM off)'],
          ['end_failed', 'End unconfirmed'],
        ]}
      />
      <FilterSelect
        label="Source"
        value={connector ?? ''}
        onChange={(v) => setConnector(v === '' ? null : v)}
        options={[
          ['', 'Any source'],
          // Exact-match values — these are the API's own spellings, not
          // display labels, so they are sent verbatim.
          ['DJI Flight Hub', 'DJI Flight Hub'],
          ['Dronesense', 'Dronesense'],
          ['aircraft_agent', 'Aircraft agent'],
          ['fh2', 'FH2'],
        ]}
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'text-[0.692em] px-2 py-0.5 rounded-full border bg-transparent outline-none transition-colors',
        value !== ''
          ? 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)'
          : 'border-(--color-border) text-(--color-text3)',
      )}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}
